import "react-sliding-pane/dist/react-sliding-pane.css";

import { Col, Row } from "react-bootstrap";
import { useEffect, useState } from "react";

import ActionPermissionGuard from "shared/components/utils/ActionPermissionGuard";
import Button from "shared/components/inputs/mui/Button";
import Card from "shared/components/data-display/card/Card";
import Loader from "shared/components/feedback/loading/Loader";
import More from "./More";
import ProgressBar from "shared/components/feedback/loading/ProgressBar";
import ProgressStatusCircle from "shared/components/feedback/progress-status-circle/ProgressStatusCircle";
import PropTypes from "prop-types";
import Text from "shared/components/data-display/mui/Text";
import env from "react-dotenv";
import { format } from "date-fns";
import moment from "moment";
import { useCrypto } from "shared/hooks/crypto/useCrypto";
import { useProjects } from "shared/services/api/useProjects";
import { useStylesForText } from "shared/styles/makeStyles";
import {
    Wrapper,
    Container,
    Title,
    TitleColumn,
    Subtitle,
    Section,
    Footer,
    ButtonLink,
    TextLink,
    MoreMenuContainer,
    StyledWrapper,
    StyledSowContent,
    StyledSowUpdatedDate,
    StyledSowStatusLabel,
} from "./ProjectDrawer.styles";

const ProjectDrawer = (props) => {
    const { getProjectDetail } = useProjects();
    const { encrypt } = useCrypto();
    const { projectId } = props;
    const [project, setProject] = useState({
        name: null,
        jobCode: null,
        client: null,
        clientFamily: null,
        startDate: null,
        endDate: null,
        status: null,
        completion: 0,
        producers: [],
        jobId: null,
        statusName: null,
        statusUpdatedDate: null,
        statusLikelihood: 0,
    });
    const classesText = useStylesForText();
    const updatedDate = format(
        new Date(project.statusUpdatedDate),
        "MMM dd, yyyy"
    );

    useEffect(() => {
        const fetchProjectDetail = async () => {
            if (projectId) {
                try {
                    const payload = await getProjectDetail([{ name: "$jobId", value: projectId }]);

                    if (!payload.error && payload.length > 0) {

                        setProject({
                            name: payload[0].JobName,
                            jobCode: payload[0].JobCode,
                            client: payload[0].Client,
                            clientFamily: payload[0].ClientFamily,
                            startDate: payload[0].JobStartDate,
                            endDate: payload[0].JobEndDate,
                            status: payload[0].SOWStatus,
                            completion: payload[0].CompletePercentage
                                ? Number.parseInt(payload[0].CompletePercentage)
                                : 0,
                            producers: payload[0].LeadProducer,
                            jobId: payload[0].JobId,
                            office: payload[0].Office,
                            retainer: payload[0].Retainer,
                            statusName: payload[0].SowStatusName,
                            statusUpdatedDate: payload[0].SOWStatusUpdated,
                            statusLikelihood: payload[0].SowStatusLikelihood,
                        });
                    }

                } catch {
                    /* */
                }
            }
        };

        fetchProjectDetail();
    }, [projectId, getProjectDetail]);

    return (
        <Wrapper>
            <Container>
                {projectId && project.name ? (
                    <>
                        <Title>
                            <Row>
                                <TitleColumn>
                                    <Text variant="largeBold">
                                        {project.name}
                                    </Text>
                                </TitleColumn>
                                <Col xs={1}>
                                    <MoreMenuContainer>
                                        <More
                                            visible
                                            infoDisabled
                                            project={project}
                                            direction="left"
                                        />
                                    </MoreMenuContainer>
                                </Col>
                            </Row>
                        </Title>
                        <Subtitle>
                            <Text
                                variant="xsmallLight"
                                className={classesText.gray2}
                            >
                                Job Code: {project.jobCode}
                            </Text>
                        </Subtitle>

                        {project.statusLikelihood !== 0 &&
                            !!project.statusLikelihood &&
                            project.statusName !== "N/A" &&
                            project.statusName !== "None" && (
                                <StyledWrapper>
                                    <StyledSowContent>
                                        <StyledSowStatusLabel
                                            variant="xsmallLight"
                                            className={classesText.gray2}
                                        >
                                            SOW Status
                                        </StyledSowStatusLabel>
                                        <ProgressStatusCircle
                                            value={project.statusLikelihood}
                                            title={project.statusName}
                                            showMessage={true}
                                        />
                                    </StyledSowContent>

                                    <StyledSowUpdatedDate>
                                        Last Updated {updatedDate}
                                    </StyledSowUpdatedDate>
                                </StyledWrapper>
                            )}

                        {project.producers.map((producer) => (
                            <Card
                                key={producer.Email ?? producer.FullName}
                                label="Lead Producer"
                                photo={
                                    producer.LargePhotoUrl &&
                                    `${env.BESTWORK_REACT_ASSET_DOMAIN}/${producer.LargePhotoUrl}`
                                }
                                name={producer.FullName}
                                title={producer.Title}
                                department={producer.Department}
                                email={producer.Email}
                            />
                        ))}
                        {(!project?.producers?.length ||
                            !project?.producers[0]?.FullName) && (
                            <Section>
                                <Text
                                    variant="xsmallLight"
                                    className={classesText.gray2}
                                >
                                    Lead Producer
                                </Text>
                                <Text variant="small">Not Assigned</Text>
                            </Section>
                        )}
                        <Section>
                            <Text
                                variant="xsmallLight"
                                className={classesText.gray2}
                            >
                                Client
                            </Text>
                            <Text variant="small">{project.client}</Text>
                        </Section>
                        <Section>
                            <Text
                                variant="xsmallLight"
                                className={classesText.gray2}
                            >
                                Client Family
                            </Text>
                            <Text variant="small">{project.clientFamily}</Text>
                        </Section>
                        <Section>
                            <Text
                                variant="xsmallLight"
                                className={classesText.gray2}
                            >
                                Retainer
                            </Text>
                            <Text variant="small">{project.retainer}</Text>
                        </Section>
                        <Section>
                            <Text
                                variant="xsmallLight"
                                className={classesText.gray2}
                            >
                                Lead Office
                            </Text>
                            <Text variant="small">{project.office}</Text>
                        </Section>
                        <Section>
                            <Text
                                variant="xsmallLight"
                                className={classesText.gray2}
                            >
                                Project Start Date
                            </Text>
                            <Text variant="small">
                                {moment(project.startDate).format("MMM D, YY")}
                            </Text>
                        </Section>
                        <Section>
                            <Text
                                variant="xsmallLight"
                                className={classesText.gray2}
                            >
                                Expected End
                            </Text>
                            <Text variant="small">
                                {moment(project.endDate).format("MMM D, YY")}
                            </Text>
                        </Section>
                        <Section>
                            <Text
                                variant="xsmallLight"
                                className={classesText.gray2}
                            >
                                Completion
                            </Text>
                            <ProgressBar
                                percent={Number.parseInt(project.completion)}
                            />
                            <Text variant="small">{project.completion}%</Text>
                        </Section>
                    </>
                ) : (
                    <Loader height="5rem" width="5rem" />
                )}
            </Container>
            <ActionPermissionGuard code="AC_VIEW_PROJECT_ACTION">
                <Footer>
                    <ButtonLink
                        href={`${globalThis.config.routes.allocation}?filters=${encrypt("jobId=" + project.jobId)}`}
                        target="_blank"
                        rel="noreferrer"
                    >
                        <Button>
                            View Project Allocations
                        </Button>
                    </ButtonLink>
                    <TextLink
                        href={
                            "/redirectto?url=" +
                            env.BESTWORK_REACT_REPORTER10_LINK +
                            "job/JobReport.aspx?JobID=" +
                            projectId +
                            "&env=" + env.ENVIRONMENT_NAME + "&vpn=true"
                        }
                        target="_blank"
                        rel="noreferrer"
                    >
                        <Text variant="xsmall">View Project Dashboard</Text>
                    </TextLink>
                </Footer>
            </ActionPermissionGuard>
        </Wrapper>
    );
};

ProjectDrawer.propTypes = {
    projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default ProjectDrawer;
